import { useState } from 'react';
import { useQuery, useMutation } from 'data-fetching-client';
import { Text } from 'foundations-components/components/Text';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { PanelNavigator } from 'foundations-components/transitional/components/PanelNavigator';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import { PanelFooter } from 'foundations-components/transitional/components/PanelFooter';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedMessageWithObjectTypeName from 'automation-ui-i18n/FormattedMessageWithObjectTypeName';
import { hasEventEnrollmentTriggers } from 'automation-ui-enrollment/utils/EnrollmentDeciders';
import { GENERATE_FILTERS_FROM_EVENT_TRIGGERS } from 'automation-ui-enrollment/queries/FlowEnrollmentQueries';
import { BULK_DELETE_BY_LIST_IDS } from 'automation-ui-enrollment/queries/ILSListQueries';
import ListSegmentationFilters from 'automation-ui-enrollment/components/common/ListSegmentationFilters';
import ManualEnrollmentByList from 'automation-ui-enrollment/components/enrollment/manual/ManualEnrollmentByList';
import EnrollCountStatusBar from 'automation-ui-enrollment/components/enrollment/manual/EnrollCountStatusBar';
import ManualEnrollmentIndividual from 'automation-ui-enrollment/components/enrollment/manual/ManualEnrollmentIndividual';
import EnrollExistingFromTriggers from 'automation-ui-enrollment/components/enrollment/manual/EnrollExistingFromTriggers';
import EnrollExistingCustom from 'automation-ui-enrollment/components/enrollment/manual/EnrollExistingCustom';
import ManualEnrollmentRadioOptions from 'automation-ui-enrollment/components/enrollment/manual/ManualEnrollmentRadioOptions';
import PreviewEnrollPanel from 'automation-ui-enrollment/components/enrollment/manual/PreviewEnrollPanel';
import useHasListsToolAccess from 'automation-ui-enrollment/hooks/useHasListsToolAccess';
import ManualEnrollmentByDataset from 'automation-ui-enrollment/components/enrollment/manual/ManualEnrollmentByDataset';
import { useIsUngated } from 'automation-ui-core/hooks/authHooks';
import { INTERACTIVE_ACTION } from 'automation-ui-enrollment/constants/Gates';
import { USER_INPUT_ACTION_TYPE_ID } from 'automation-ui-enrollment/constants/flowInteraction';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ENROLLMENT_PANEL_WIDTH = 750;
const ENROLL_FROM_LIST = 'ENROLL_FROM_LIST';
const ENROLL_INDIVIDUAL = 'ENROLL_INDIVIDUAL';
const ENROLL_FROM_TRIGGERS = 'ENROLL_FROM_TRIGGERS';
const ENROLL_CUSTOM = 'ENROLL_CUSTOM';
const ENROLL_FROM_DATASET = 'ENROLL_FROM_DATASET';
const EnrollSaveHandlers = {
  ENROLL_FROM_LIST: ({
    objectType,
    listId,
    onConfirm
  }) => {
    onConfirm({
      objectTypeId: objectType,
      selectedObjects: [],
      listId,
      mode: 'LIST'
    });
  },
  ENROLL_FROM_DATASET: ({
    objectType,
    datasetId,
    onConfirm
  }) => {
    onConfirm({
      objectTypeId: objectType,
      selectedObjects: [],
      datasetId,
      mode: 'DATASET'
    });
  },
  ENROLL_INDIVIDUAL: ({
    objectType,
    selectedObjects,
    onConfirm
  }) => {
    onConfirm({
      objectTypeId: objectType,
      selectedObjects: selectedObjects,
      mode: 'INDIVIDUAL'
    });
  },
  ENROLL_FROM_TRIGGERS: ({
    objectType,
    filterBranch,
    useRefinementFilters,
    onConfirm
  }) => {
    onConfirm({
      objectTypeId: objectType,
      selectedObjects: [],
      filterBranch,
      useRefinementFilters,
      mode: 'TRIGGERS'
    });
  },
  ENROLL_CUSTOM: ({
    objectType,
    filterBranch,
    onConfirm
  }) => {
    onConfirm({
      objectTypeId: objectType,
      selectedObjects: [],
      filterBranch,
      mode: 'CUSTOM'
    });
  }
};
const isSaveButtonDisabled = ({
  selectedListId,
  selectedDatasetId,
  selectedObjects,
  customFilters,
  enrollOption
}) => {
  if (enrollOption === ENROLL_FROM_TRIGGERS) {
    return false;
  }
  if (enrollOption === ENROLL_FROM_DATASET) {
    return !selectedDatasetId;
  }
  if (customFilters) {
    return false;
  }
  if (selectedObjects) {
    return selectedObjects.length === 0;
  }
  return !selectedListId;
};
export const getInitialEnrollOption = ({
  presetState
}) => {
  if (presetState) {
    if (presetState.listId) {
      return ENROLL_FROM_LIST;
    } else if (presetState.selectedObjects && presetState.selectedObjects.length > 0) {
      return ENROLL_INDIVIDUAL;
    } else if (presetState.filterBranch) {
      if (presetState.hasCustomFilters) {
        return ENROLL_CUSTOM;
      } else {
        return ENROLL_FROM_TRIGGERS;
      }
    } else if (presetState.datasetId) {
      return ENROLL_FROM_DATASET;
    }
  }
  return undefined;
};
const ManualEnrollmentPanel = ({
  onClose,
  objectTypeId,
  onConfirm,
  mode,
  flow,
  presetState
}) => {
  const [selectedListId, setSelectedListId] = useState(presetState ? presetState.listId : undefined);
  const [selectedDatasetId, setSelectedDatasetId] = useState(presetState ? presetState.datasetId : undefined);
  const [selectedSubviewId, setSelectedSubviewId] = useState(presetState ? presetState.subviewId : undefined);
  const [selectedObjects, setSelectedObjects] = useState(presetState ? presetState.selectedObjects : undefined);
  const [useRefinementFilters, setUseRefinementFilters] = useState(presetState ? presetState.useRefinementFilters : false);
  const [customFilters, setCustomFilters] = useState(presetState ? presetState.filterBranch : undefined);
  const [filterCache, setFilterCache] = useState(new Map());
  const [isEnrolling, setIsEnrolling] = useState(false);
  const hasListsToolAccess = useHasListsToolAccess(objectTypeId);
  const isUngatedToInteractiveAction = useIsUngated(INTERACTIVE_ACTION);
  const isInteractive = isUngatedToInteractiveAction && Object.values(flow.actions).some(({
    actionTypeId
  }) => actionTypeId === USER_INPUT_ACTION_TYPE_ID);
  const [enrollOption, setEnrollOption] = useState(isInteractive ? ENROLL_INDIVIDUAL : getInitialEnrollOption({
    presetState
  }));
  const [currentPanel, setCurrentPanel] = useState(presetState && presetState.previewPanelOpen ? 'PREVIEW_PANEL' : 'PRIMARY_PANEL');
  const [scrollContainer, setScrollContainer] = useState();
  const enrollHandler = EnrollSaveHandlers[enrollOption];
  const setScrollContainerIgnoringNull = scrollContainerElement => {
    // skipping updates when the container is briefly null on component reloads
    if (scrollContainerElement !== null) {
      setScrollContainer(scrollContainerElement);
    }
  };
  const {
    data: generatedFilterBranchData,
    error
  } = useQuery(GENERATE_FILTERS_FROM_EVENT_TRIGGERS, {
    variables: {
      useRefinementCriteria: useRefinementFilters,
      associatedLists: flow.associatedLists,
      flowEventFilters: flow.flowEventFilters
    },
    skip: !hasEventEnrollmentTriggers(flow)
  });
  const [bulkDeleteLists] = useMutation(BULK_DELETE_BY_LIST_IDS);
  const handleCleanupLists = filterToListIdCache => {
    const listIds = Array.from(filterToListIdCache.values());

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    bulkDeleteLists({
      variables: {
        listIds
      }
    });
  };
  const updateCache = (key, value) => {
    if (!filterCache.has(key)) {
      setFilterCache(new Map(filterCache.set(key, value)));
    }
  };
  return /*#__PURE__*/_jsxs(PanelNavigator, {
    currentPanel: currentPanel,
    onPreviousClick: () => setCurrentPanel('PRIMARY_PANEL'),
    children: [/*#__PURE__*/_jsxs(Panel, {
      width: ENROLLMENT_PANEL_WIDTH,
      panelKey: "PRIMARY_PANEL",
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(PanelHeader, {
        children: /*#__PURE__*/_jsx(Text, {
          as: "h2",
          children: mode === 'MANUAL' ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-enrollment.redesign.enrollExistingPanel.title"
          }) : /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
            baseLangKey: "automation-ui-enrollment.redesign.events.enrollExisting.panelTitle_cob",
            objectTypeId: objectTypeId
          })
        })
      }), /*#__PURE__*/_jsx(PanelBody, {
        ref: setScrollContainerIgnoringNull,
        children: /*#__PURE__*/_jsxs(_Fragment, {
          children: [!isInteractive && /*#__PURE__*/_jsxs(PanelSection, {
            children: [/*#__PURE__*/_jsx(Text, {
              as: "h5",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "automation-ui-enrollment.redesign.enrollExistingPanel.description"
              })
            }), /*#__PURE__*/_jsx(ManualEnrollmentRadioOptions, {
              objectTypeId: objectTypeId,
              selectedOption: enrollOption,
              handleOptionChange: newEnrollOption => {
                setEnrollOption(newEnrollOption);
                setSelectedListId(undefined);
                setSelectedDatasetId(undefined);
                setSelectedSubviewId(undefined);
                setCustomFilters(undefined);
                if (newEnrollOption === ENROLL_FROM_LIST || newEnrollOption === ENROLL_CUSTOM || newEnrollOption === ENROLL_FROM_DATASET) {
                  setSelectedObjects(undefined);
                } else {
                  setSelectedObjects([]);
                }
              },
              canEnrollFromExistingTriggers: !!(generatedFilterBranchData && !generatedFilterBranchData.filtersFromCurrentTriggers.error) && !error,
              isEventBasedWorkflow: flow.enrollmentTrigger === 'EVENT'
            })]
          }), enrollOption === ENROLL_FROM_LIST && hasListsToolAccess && /*#__PURE__*/_jsx(ManualEnrollmentByList, {
            objectTypeId: objectTypeId,
            handleListSelect: setSelectedListId,
            selectedListId: selectedListId,
            mode: mode
          }), enrollOption === ENROLL_FROM_DATASET && /*#__PURE__*/_jsx(ManualEnrollmentByDataset, {
            objectTypeId: objectTypeId,
            handleDatasetSelect: ({
              target: {
                value: {
                  datasetId,
                  subviewId
                }
              }
            }) => {
              setSelectedDatasetId(datasetId);
              setSelectedSubviewId(subviewId);
            },
            selectedDatasetId: selectedDatasetId,
            selectedSubviewId: selectedSubviewId,
            mode: mode
          }), enrollOption === ENROLL_INDIVIDUAL && /*#__PURE__*/_jsx(ManualEnrollmentIndividual, {
            objectTypeId: objectTypeId,
            handleObjectSelection: setSelectedObjects,
            selectedObjects: selectedObjects,
            scrollContainer: scrollContainer,
            mode: mode,
            isMultiSelect: !isInteractive
          }), enrollOption === ENROLL_FROM_TRIGGERS && generatedFilterBranchData && /*#__PURE__*/_jsx(EnrollExistingFromTriggers, {
            flow: flow,
            filtersFromCurrentTriggers: generatedFilterBranchData.filtersFromCurrentTriggers,
            useRefinementFilters: useRefinementFilters,
            setUseRefinementFilters: setUseRefinementFilters,
            mode: mode
          }), enrollOption === ENROLL_CUSTOM && /*#__PURE__*/_jsx(EnrollExistingCustom, {
            flow: flow,
            mode: mode,
            openFiltersPanel: () => setCurrentPanel('CUSTOM_FILTERS_PANEL'),
            customFilters: customFilters
          })]
        })
      }), (selectedListId || customFilters || selectedObjects) && /*#__PURE__*/_jsx(EnrollCountStatusBar, {
        objectTypeId: objectTypeId,
        enrollMode: enrollOption,
        listId: selectedListId,
        filterBranch: customFilters || (generatedFilterBranchData ? generatedFilterBranchData.filtersFromCurrentTriggers.filterBranch : undefined),
        updateFilterCache: updateCache,
        filterCache: filterCache,
        objectCount: selectedObjects && selectedObjects.length,
        handleOpenPreviewPanel: listId => {
          if (listId) {
            setSelectedListId(listId);
          }
          setCurrentPanel('PREVIEW_PANEL');
        },
        flow: flow
      }), /*#__PURE__*/_jsxs(PanelFooter, {
        children: [/*#__PURE__*/_jsx(UILoadingButton, {
          use: "primary"
          // this is slightly redundant logic but is needed to trigger an update to the disabled state
          // if listId is changed through presetState props
          ,
          loading: isEnrolling,
          disabled: !selectedListId && isSaveButtonDisabled({
            selectedListId,
            selectedDatasetId,
            selectedObjects,
            customFilters,
            enrollOption
          }),
          onClick: () => {
            setIsEnrolling(true);
            handleCleanupLists(filterCache);
            enrollHandler({
              listId: selectedListId,
              datasetId: selectedDatasetId,
              selectedObjects,
              objectType: objectTypeId,
              filterBranch: customFilters || generatedFilterBranchData && generatedFilterBranchData.filtersFromCurrentTriggers.filterBranch,
              useRefinementFilters,
              onConfirm
            });
          },
          children: mode === 'EXISTING' ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-enrollment.redesign.enrollExistingPanel.saveButtonExisting"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-enrollment.redesign.enrollExistingPanel.saveButton"
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          onClick: onClose,
          use: "tertiary-light",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-enrollment.redesign.enrollExistingPanel.cancelButton"
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs(Panel, {
      width: ENROLLMENT_PANEL_WIDTH,
      panelKey: "PREVIEW_PANEL",
      children: [/*#__PURE__*/_jsx(PanelHeader, {
        children: /*#__PURE__*/_jsx(Text, {
          as: "h2",
          children: /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
            baseLangKey: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollFromList.link_cob",
            objectTypeId: objectTypeId
          })
        })
      }), /*#__PURE__*/_jsx(PanelBody, {
        ref: setScrollContainerIgnoringNull,
        children: (selectedListId || selectedObjects) && /*#__PURE__*/_jsx(PreviewEnrollPanel, {
          objectTypeId: objectTypeId,
          listId: selectedListId,
          objectIds: selectedObjects,
          scrollContainer: scrollContainer
        })
      })]
    }), /*#__PURE__*/_jsx(Panel, {
      width: 400,
      panelKey: "CUSTOM_FILTERS_PANEL",
      children: /*#__PURE__*/_jsx(ListSegmentationFilters, {
        value: customFilters,
        readOnly: false,
        objectTypeId: objectTypeId,
        onChange: newFilters => setCustomFilters(newFilters),
        layout: "panel",
        onClosePanel: onClose
      })
    })]
  });
};
export default ManualEnrollmentPanel;